import posthog from 'posthog-js'

import DotsLoading from 'components/loadings/dotsLoading'
import { useShareNumber } from './useShareNumber'

interface Props {
    /** Fill the phone field WITHOUT sending anything. */
    onPrefill: (phone: string) => void
    /** Give up waiting and start the ordinary SMS login for this number. */
    onUseOtp: (phone: string) => void
    disabled?: boolean
}

/**
 * "Use the number on this account" — the small button under the phone field.
 *
 * Inside a messenger the first thing a visitor sees is the big one
 * (`MessengerLogin`); this is the way back for someone who chose «با شمارهٔ
 * دیگری وارد شوید» and changed their mind. Same flow either way — see
 * `useShareNumber`.
 */
export default function ShareNumberButton({
    onPrefill,
    onUseOtp,
    disabled,
}: Props) {
    const { available, stage, phone, share } = useShareNumber({
        variant: 'inline',
        onPrefill,
        declinedMessage: 'شماره دریافت نشد؛ لطفاً شمارهٔ خود را وارد کنید.',
    })

    if (!available) return null

    if (stage === 'waiting' || stage === 'requesting') {
        return (
            <div className="mt-4 flex flex-col items-center gap-2 text-center">
                <DotsLoading />
                <p className="text-sm">در حال ورود به همیان…</p>
                <p className="text-xs text-text-gray leading-6">
                    شمارهٔ شما در حال بررسی است. چند لحظه صبر کنید.
                </p>
            </div>
        )
    }

    if (stage === 'fallback') {
        return (
            <div className="mt-4 flex flex-col gap-2 text-center">
                <p className="text-xs text-text-gray leading-6">
                    ورود خودکار انجام نشد. با کد پیامکی وارد شوید:
                </p>
                <button
                    type="button"
                    onClick={() => {
                        posthog.capture('miniapp_share_number_otp_fallback', {
                            variant: 'inline',
                        })
                        // `phone` is guaranteed here: stage only becomes
                        // 'fallback' when we actually have a number.
                        if (phone) onUseOtp(phone)
                    }}
                    className="w-full bg-green-1 text-white rounded-md py-2 text-sm"
                >
                    ورود با کد پیامکی
                </button>
            </div>
        )
    }

    return (
        <button
            type="button"
            onClick={share}
            disabled={disabled}
            className="w-full mt-3 border border-green-1 text-green-1 rounded-md py-2 text-sm disabled:opacity-60"
        >
            استفاده از شمارهٔ همین حساب
        </button>
    )
}
